var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { format } from 'date-fns';
import CellTooltip from './CellTooltip';
import { httpClient } from '../services/http/httpClient';
import { addFieldDefaults, initialiseFieldMeta } from './Field';
import { isFloat, normaliseCaps } from "../general/Utils";
import Relationship from './Relationship';
import { Status } from '../general';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
export var fieldMetaVersion = "field-meta-v8";
var idField; // id or uid
var idFieldDefinedPreviously = false;
var hiddenFields = false;
export function isRelationship(key) {
    return key.includes('.');
}
export function addTotalText(totalSize) {
    if (totalSize === 1) {
        return "1 Row";
        // add a plus for elastic search (results cap at 10,000)
    }
    else if (totalSize === 10000) {
        return "10000+ Rows";
    }
    return totalSize.toString() + " Rows";
}
function createLink(text, url) {
    return _jsx("a", __assign({ href: url, target: "_blank", rel: "noopener noreferrer" }, { children: text }));
}
function createRelationshipBox(key, data, baseUrl, detail) {
    var _a = key.split('.'), relationship = _a[0], attribute = _a[1];
    // cannot assume some keys exist
    if ('relationships' in data) {
        if (relationship in data['relationships']) {
            var relationData = data['relationships'][relationship]['data'];
            // need to create attributes for id to be added to if it doesn't exist
            if (!('attributes' in relationData)) {
                relationData['attributes'] = {};
            }
            relationData['attributes']['id'] = relationData['id'];
            if (attribute in relationData['attributes']) {
                return (_jsx(Relationship, { attribute: attribute, data: relationData, detail: detail, baseUrl: baseUrl }));
            }
        }
    }
    return "";
}
function createDate(value) {
    var date = new Date(value);
    var dateText = format(date, 'dd/MM/yyyy');
    var dateContents = format(date, 'dd/MM/yyyy HH:mm');
    return (_jsx(CellTooltip, { followCursor: true, value: dateText, contents: dateContents }));
}
function createBoolean(value) {
    switch (value) {
        case true:
            return (_jsx(Status, { text: "True", status: "success" }));
        case false:
            return (_jsx(Status, { text: "False", status: "danger" }));
        default:
            return "";
    }
}
function createImage(value) {
    return (_jsx("a", __assign({ href: value, target: "_blank", rel: "noopener noreferrer" }, { children: _jsx("img", { src: value, alt: value, width: "30%" }) })));
}
function createFormattedList(list) {
    return (_jsx("div", __assign({ className: 'simple-tag-container' }, { children: list.map(function (value) {
            return (_jsx("div", __assign({ className: 'simple-tag' }, { children: value }), value));
        }) })));
}
function createExpander(value) {
    var shortValue = (_jsxs("div", __assign({ className: "copy-icon" }, { children: [value.substring(0, 32) + "...", _jsx(FontAwesomeIcon, { icon: faCopy, size: "sm", onClick: function () {
                    navigator.clipboard.writeText(value);
                } })] })));
    return (_jsx(CellTooltip, { value: shortValue, contents: value }));
}
function createFloat(value) {
    return (_jsx(CellTooltip, { followCursor: true, value: value.toFixed(2), contents: value }));
}
function createCellRenderer(cellRenderer, key, value, data, baseUrl) {
    if (cellRenderer === null)
        return value;
    if (typeof cellRenderer === 'string') {
        if (value === null || value === undefined)
            return "";
        if (cellRenderer === 'relationship') {
            return createRelationshipBox(key, data, baseUrl);
        }
        else if (cellRenderer === 'relationshipDetail') {
            return createRelationshipBox(key, data, baseUrl, true);
        }
        else if (cellRenderer === 'datetime') {
            return createDate(value);
        }
        else if (cellRenderer === 'boolean') {
            return createBoolean(value);
        }
        else if (cellRenderer === 'image') {
            return createImage(value);
        }
        else if (cellRenderer === 'list') {
            return createFormattedList(value);
        }
        else if (cellRenderer === 'expander') {
            return createExpander(value);
        }
        else if (cellRenderer === 'float') {
            return createFloat(value);
        }
    }
    var props = {};
    if (cellRenderer.propPointers !== undefined) {
        for (var _i = 0, _a = Object.entries(cellRenderer.propPointers); _i < _a.length; _i++) {
            var _b = _a[_i], prop = _b[0], requiredField = _b[1];
            if (requiredField === 'id') {
                props[prop] = data['id'];
            }
            else {
                props[prop] = data["attributes"][requiredField];
            }
        }
    }
    if (cellRenderer.props !== undefined) {
        Object.assign(props, cellRenderer.props);
    }
    // all row data always passed to use in a cellRenderer component via a rowData prop
    props["rowData"] = data;
    return _jsx(cellRenderer.element, __assign({}, props));
}
function setValueBasedCellRenderer(key, value, fieldMetaData) {
    if (fieldMetaData[key].cellRenderer === undefined) {
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                fieldMetaData[key].cellRenderer = 'list';
            }
            else if (value.length > 32) {
                fieldMetaData[key].cellRenderer = 'expander';
            }
            else if (isFloat(value)) {
                fieldMetaData[key].cellRenderer = 'float';
            }
        }
    }
}
function addCustomCellRendererData(fieldMetaData, attributes) {
    for (var _i = 0, _a = Object.keys(fieldMetaData); _i < _a.length; _i++) {
        var key = _a[_i];
        if (fieldMetaData[key].custom) {
            attributes[key] = 'CUSTOM_FIELD';
        }
    }
    return attributes;
}
function formatAttributeData(row, fieldMetaData, rowOutput, baseUrl) {
    var attributes = row["attributes"];
    // add non-null value for a custom field to allow cellRenderer to display
    addCustomCellRendererData(fieldMetaData, attributes);
    for (var _i = 0, _a = Object.entries(attributes); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], value = _b[1];
        if (fieldMetaData[key] !== undefined) {
            setValueBasedCellRenderer(key, value, fieldMetaData);
            if (fieldMetaData[key].cellRenderer !== undefined) {
                rowOutput[key] = createCellRenderer(fieldMetaData[key].cellRenderer, key, value, row, baseUrl);
            }
            else if (fieldMetaData[key].link !== undefined) {
                rowOutput[key] = createLink(attributes[key], attributes[fieldMetaData[key].link]);
            }
            else {
                rowOutput[key] = value;
            }
        }
    }
}
function addRelationshipFieldsToAttributes(row, fieldMetaData) {
    var rowRelationships = row["relationships"];
    var rowAttributes = row["attributes"];
    for (var _i = 0, _a = Object.entries(fieldMetaData); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], meta = _b[1];
        // only deal with relationships
        if (!meta.isAttribute) {
            var _c = key.split('.'), relationship = _c[0], attribute = _c[1];
            // cannot assume relationship exists
            if (relationship in rowRelationships && rowRelationships[relationship]) {
                if ("data" in rowRelationships[relationship]) {
                    var instanceData = rowRelationships[relationship]["data"];
                    if (attribute === "id") {
                        rowAttributes[key] = instanceData["id"];
                    }
                    else if ("attributes" in instanceData && attribute in instanceData["attributes"]) {
                        rowAttributes[key] = instanceData["attributes"][attribute];
                    }
                }
            }
            // if row doesn't have the fields data, default to null
            if (!rowAttributes[key])
                rowAttributes[key] = null;
        }
    }
}
export function convertTableData(data, fieldMeta, baseUrl) {
    if (data[0] === undefined)
        return [];
    var updatedData = [];
    data.forEach(function (row) {
        // create empty attributes if they don't exist
        if (!('attributes' in row))
            row['attributes'] = {};
        if ('relationships' in row) {
            addRelationshipFieldsToAttributes(row, fieldMeta.data);
        }
        var rowOutput = { 'id': row.id };
        if ('attributes' in row) {
            formatAttributeData(row, fieldMeta.data, rowOutput, baseUrl);
        }
        updatedData.push(rowOutput);
    });
    return updatedData;
}
function addDefaultCellRenderer(key, type) {
    // relationship ids have relationship boxes by default
    if (isRelationship(key) && key.split('.')[1] === 'id') {
        return 'relationship';
    }
    switch (type) {
        case 'datetime':
        case 'boolean':
            return type;
    }
    return undefined;
}
function structureFieldMetaViaProp(fieldMeta, fields) {
    for (var _i = 0, _a = Object.entries(fields); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], meta = _b[1];
        var isActive = (meta.hidden) ? 'inactive' : 'active';
        fieldMeta.order[isActive].push(key);
        fieldMeta.data[key] = addFieldDefaults(meta);
        fieldMeta.data[key].isAttribute = !isRelationship(key);
        if (fieldMeta.data[key].hidden)
            hiddenFields = true;
    }
}
function addRemoteFilterType(type, cardinality) {
    if (cardinality && cardinality < 20 && type === 'str')
        return 'multi';
    if (type === 'double')
        return 'float';
    return type;
}
function addRemoteTypesAndExtraColumns(endpoint, fieldMeta, entityMeta, fieldPropExists) {
    idFieldDefinedPreviously = idField in fieldMeta.data;
    for (var _i = 0, _a = Object.entries(entityMeta.flatAttributes[endpoint]); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], meta = _b[1];
        var hidden = fieldPropExists;
        var isActive = hidden ? 'inactive' : 'active';
        var type = meta['python_type'];
        var rename = meta['display_name'];
        var description = meta['description'];
        var filterType = addRemoteFilterType(type, meta['cardinality']);
        // auto add field that are not yet in fieldMeta
        if (!hiddenFields && !(key in fieldMeta.data)) {
            // relationship attributes are hidden by default
            if (isRelationship(key) && key.split('.')[1] !== 'id') {
                hidden = true;
                isActive = 'inactive';
            }
            if (key !== idField)
                fieldMeta.order[isActive].push(key);
            fieldMeta.data[key] = addFieldDefaults(
            // hidden as default, overridden by field prop
            { isAttribute: !isRelationship(key), hidden: hidden });
        }
        // add deafults to fields fields
        if (key in fieldMeta.data) {
            fieldMeta.data[key].type = fieldMeta.data[key].type || type;
            fieldMeta.data[key].filter = fieldMeta.data[key].filter || filterType;
            fieldMeta.data[key].sort = fieldMeta.data[key].sort || true;
            fieldMeta.data[key].rename = fieldMeta.data[key].rename || rename || normaliseCaps(key, endpoint);
            fieldMeta.data[key].description = fieldMeta.data[key].description || description;
        }
    }
}
function addDefaultMeta(fieldMeta, fieldPropExists) {
    // add defaults
    for (var _i = 0, _a = Object.entries(fieldMeta.data); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], meta = _b[1];
        if (fieldMeta.data[key].cellRenderer === undefined) {
            fieldMeta.data[key].cellRenderer = addDefaultCellRenderer(key, meta.type);
        }
    }
    // ensure fields are easy to find
    fieldMeta.order.inactive.sort();
    // id shoud be first in the order (initial load) and no hidden fields defined
    if (idField !== undefined && !idFieldDefinedPreviously && !hiddenFields) {
        var isActive = fieldPropExists ? 'inactive' : 'active';
        fieldMeta.order[isActive].unshift(idField);
    }
}
export function structureFieldMeta(endpoint, entityMeta, fields, pageSize) {
    endpoint = endpoint.split('/').pop();
    var fieldMeta = initialiseFieldMeta(pageSize);
    var fieldPropExists = fields !== undefined;
    if (fieldPropExists) {
        structureFieldMetaViaProp(fieldMeta, fields);
    }
    /*
    --- dealing with id/uid ---
    - id is seperate from attributes, so it needs to be added
    - only added if uid does not exist
    - value can be null, as only the key is used in this instance
    */
    if (entityMeta !== undefined) {
        idField = ('uid' in entityMeta.flatAttributes[endpoint]) ? 'uid' : 'id';
        addRemoteTypesAndExtraColumns(endpoint, fieldMeta, entityMeta, fieldPropExists);
    }
    addDefaultMeta(fieldMeta, fieldPropExists);
    return fieldMeta;
}
export function tableDebug(apiData, fieldMeta, debug) {
    if (debug) {
        try {
            var fieldPossibilities = {
                'attributes': ['id'],
                'relationships': []
            };
            var apiDataInstance = apiData[0];
            if ('attributes' in apiDataInstance) {
                for (var _i = 0, _a = Object.keys(apiDataInstance.attributes); _i < _a.length; _i++) {
                    var key = _a[_i];
                    fieldPossibilities['attributes'].push(key);
                }
            }
            var relationships = apiDataInstance.relationships;
            if ('relationships' in apiDataInstance) {
                for (var _b = 0, _c = Object.entries(relationships); _b < _c.length; _b++) {
                    var _d = _c[_b], key = _d[0], value = _d[1];
                    // ignoring one-to-many relationships
                    if ('data' in value) {
                        fieldPossibilities['relationships'].push(key);
                    }
                }
            }
            console.log('Field Possibilities', fieldPossibilities);
            console.log('Api Response Data', apiData);
            console.log('Field Meta', fieldMeta);
        }
        catch (e) { } // eslint-disable-line
    }
}
export function createSort(sortColumn, sortType) {
    if (sortType === 'desc') {
        return "-" + sortColumn;
    }
    return sortColumn;
}
export function setFieldMetaAttributeInStorage(tableId, value, attribute) {
    if (attribute === undefined) {
        localStorage.setItem("".concat(tableId, "-").concat(fieldMetaVersion), JSON.stringify(value));
    }
    else {
        var fieldMeta = JSON.parse(localStorage.getItem("".concat(tableId, "-").concat(fieldMetaVersion)));
        fieldMeta[attribute] = value;
        localStorage.setItem("".concat(tableId, "-").concat(fieldMetaVersion), JSON.stringify(fieldMeta));
    }
}
export function getFieldMetaAttributeFromStorage(tableId, fields, attribute) {
    var data = localStorage.getItem("".concat(tableId, "-").concat(fieldMetaVersion));
    if (data !== null) {
        var fieldMeta = JSON.parse(data);
        if (attribute !== undefined) {
            return fieldMeta[attribute];
        }
        else if (fields !== undefined) {
            fieldMeta = fieldMetaToCellRenderer(fields, fieldMeta);
        }
        return fieldMeta;
    }
    return null;
}
export function deleteFieldMetaFromStorage(tableId) {
    localStorage.removeItem("".concat(tableId, "-").concat(fieldMetaVersion));
    window.location.reload();
}
export function fieldMetaToCellRenderer(fields, fieldMeta) {
    for (var field in fields) {
        if (fields[field].cellRenderer) {
            fieldMeta.data[field].cellRenderer = fields[field].cellRenderer;
        }
    }
    return fieldMeta;
}
export function exportTableToSpreadsheet(endpoint, fieldMetaData, filter, sortColumn, sortType, setSuccess, setError, setDownloading, defaultSort, baseUrl) {
    setDownloading(true);
    var columns = Object.keys(fieldMetaData).map(function (key) { return ({
        key: key,
        display_name: fieldMetaData[key].rename,
        hidden: fieldMetaData[key].hidden
    }); });
    var params = {
        page_size: 10000,
        filter: filter
    };
    // deal with sorting
    if (sortColumn !== '') {
        params['sort_by'] = createSort(sortColumn, sortType);
    }
    else if (defaultSort !== undefined) {
        params['sort_by'] = defaultSort;
    }
    httpClient().post('/' + endpoint + ':export', { data: columns }, {
        params: params,
        baseURL: baseUrl,
        responseType: 'blob'
    })
        .then(function (res) {
        // temporary URL for the blob
        var tempUrl = window.URL.createObjectURL(res.data);
        // Trigger the download with an anchor element
        var a = document.createElement('a');
        a.href = tempUrl;
        a.download = endpoint + '_table.xlsx';
        a.click();
        // Release the URL
        window.URL.revokeObjectURL(tempUrl);
        setDownloading(false);
        if (res.status !== 200)
            throw Error();
        setDownloading(false);
        setSuccess('Download Completed');
    }).catch(function (error) {
        setDownloading(false);
        setError("Download Failed: " + error.message);
    });
}
